// ADR 30.9.26l §4.1 — one slot, two strips.
//
// `AdaptiveFrame` is at its line budget, so the consent strip takes the
// install strip's line rather than a new one: this slot decides between them,
// and there is never more than one strip. Consent comes first; the install
// strip waits until the visitor has answered.
//
// The consent strip shows on `/s/:token`, compact. That is a deliberate
// carve-out from "a shared plan carries no app chrome" (8.9.26i AC4), which
// `InstallStripSlot` still honours: a consent question is not app chrome.
//
// While the session is `loading` it renders nothing — no install-strip flash
// either — because `userId` is `null` then too, and an Accept that skipped the
// `PUT` for a restoring learner would be re-denied by the next reconcile.
//
// ADR 6.10.26a §5 — one exception to that order. An `?install=1` request
// (held `pending` or `open`) renders the requested install strip first, ahead
// of the `loading` null and of the consent check, until the strip is closed or
// the tab reloads; the install strip collects nothing, so nothing is collected
// out of order. Skipped on a shared plan and while the affordance is `none`
// (the request then stays `pending` for a later affordance). This slot is the
// request's one consumer because it is mounted on every shell route.

import { useEffect } from 'react'
import { useMatch } from 'react-router-dom'

import { useAuthSession } from '../auth/session'
import { closeInstallRequest } from '../pwa/installPrompt'
import { InstallStripSlot } from '../pwa/InstallStrip'
import { InstallBoundary } from '../telemetry/field/InstallBoundary'
import { useInstallAffordance, useInstallRequest } from '../pwa/useInstallAffordance'
import { ConsentStrip } from './ConsentStrip'
import { useConsentAnswered } from './consentAsked'

/**
 * ADR 15.10.26d §2 — this slot calls `useInstallAffordance()` itself, so a
 * throw in the UA parse would reach `RootErrorBoundary` from here first. The
 * cost: a throw here also hides the consent strip for that page load, and
 * nothing is collected meanwhile because consent defaults to denied.
 */
export function TopStripSlot() {
  return (
    <InstallBoundary surface="top_slot">
      <TopStripSlotBody />
    </InstallBoundary>
  )
}

function TopStripSlotBody() {
  const answered = useConsentAnswered()
  const { status } = useAuthSession()
  const onShared = useMatch('/s/:token') !== null
  const request = useInstallRequest()
  const { affordance } = useInstallAffordance()
  // After an install the affordance turns `none` and the strip has already
  // unmounted, so its × cannot close an open request; this does.
  useEffect(() => {
    if (request === 'open' && affordance === 'none') closeInstallRequest()
  }, [request, affordance])
  if (request !== 'none' && affordance !== 'none' && !onShared) return <InstallStripSlot requested />
  if (answered) return <InstallStripSlot />
  if (status === 'loading') return null
  return <ConsentStrip compact={onShared} />
}
